/* eslint-disable @typescript-eslint/member-ordering */

import { _decorator, UIRenderer, Mat4, Node, Vec2, Vec3, Quat, renderer, CCFloat, Enum } from 'cc';
// IAssemblerManager 是介面，必須用 import type，否則 build 的 transpiler
// 可能把它當成執行期的具名匯出去解析（cc 沒有這個名字）。
import type { IAssemblerManager } from 'cc';
import { DEV } from 'cc/env';

const { ccclass, property } = _decorator;

// Cached temporary values shared by every Neutrino component.
const gnwprMat0 = new Mat4();
const gnwprMat1 = new Mat4();
const gnwprQuat0 = new Quat();
const gnwprQuat1 = new Quat();
const gnwprVec0 = new Vec3();
const EPSILON = 0.000001;

export enum NeutrinoEventType {
    TextureLoaded = 'TextureLoaded',
    Restart = 'NeutrinoRestart',
    Paused = 'NeutrinoPaused',
    Resumed = 'NeutrinoResumed',
    EmitterPaused = 'NeutrinoEmitterPaused',
    EmitterResumed = 'NeutrinoEmitterResumed',
    ResetPosition = 'NeutrinoResetPosition',
    ParticlesAllDisappear = 'NeutrinoParticlesAllDisappear',
}

/**
 * 所有 Neutrino 粒子元件的共用基底。
 *
 * v1.0（`NeutrinoComponent`）與 v1.1（`v1_1/NeutrinoComponentV1_1`）都繼承這個類別，
 * 對外的屬性、事件與 API 完全一致，差異只在底層的模擬與渲染實作。
 *
 * 外部程式（TintNeutrino / NeutrinoShaderManipulator / NeutrinoParticleAction /
 * EnhanceAnimationClip / BindNeutrino*）一律相依這個基底，才能同時接受兩個版本。
 */
@ccclass('NeutrinoComponentBase')
export default abstract class NeutrinoComponentBase extends UIRenderer {
    public static readonly EVENT_TYPE = NeutrinoEventType;

    /**
     * 由 NeutrinoAssembler2D 於載入時指派（兩個版本共用同一個空 assembler）。
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention -- 對齊 Cocos 的 UIRenderer.Assembler
    public static Assembler: IAssemblerManager = null;

    // We use own materials list, ignoring 'this.materials' because of some strange
    // bug in 3.4.2 when one of material instances in materials list is deleted by
    // the preview process in the editor. As a result we can't see materials assigned
    // to the component in the editor.
    public privateMaterials: renderer.MaterialInstance[] = null;

    /** 由 codegen 產生，列出這個特效用到的貼圖名稱（同時作為材質下拉選單的來源）。 */
    public abstract readonly textureList: string[];

    protected readonly nwpos: number[] = [0, 0, 0, 0];
    protected readonly nwrot: number[] = [0, 0, 0, 0];
    protected lastParticleCount: number = 0;

    private _materialsEnum: { [key: string]: number } | null = null;
    private _worldScale = new Vec3();

    @property
    protected readonly recycleOnDisable = false;

    @property
    protected _pause = false;

    @property({
        tooltip: DEV && '暫停更新整個粒子系統',
    })
    public get pause() {
        return this._pause;
    }

    public set pause(value) {
        if (this._pause === value) {
            return;
        }
        this._pause = value;
        if (this._pause) {
            this.pauseSystem();
        } else {
            this.resumeSystem();
        }
    }

    @property
    protected _pauseEmitter = false;

    @property({
        tooltip: DEV && '暫停發射粒子',
    })
    public get pauseEmitter() {
        return this._pauseEmitter;
    }

    public set pauseEmitter(value) {
        if (this._pauseEmitter === value) {
            return;
        }
        this._pauseEmitter = value;
        if (this._pauseEmitter) {
            this.pauseGeneratorsSystem();
        } else {
            this.resumeGeneratorsSystem();
        }
    }

    @property({
        tooltip: DEV && '速度倍率',
        type: CCFloat,
    })
    public timeScale: number = 1.0;

    @property(CCFloat)
    protected _fps: number = 0.0333333;

    @property({
        type: CCFloat,
        tooltip: DEV && '限制粒子系統更新頻率, 0 或負數表示不限制。',
    })
    public get fps() {
        return this._fps;
    }

    public set fps(value) {
        this._fps = value;
        this.applyFps(value);
    }

    @property(Node)
    protected _worldParent: Node | null = null;

    @property({
        type: Node,
        tooltip: DEV && 'This object defines world coordinate system for the effect. If null, the scene is considered as world parent.',
    })
    public get worldParent() {
        return this._worldParent;
    }

    public set worldParent(value) {
        this._worldParent = value;
        this.waitForContextAndReset();
    }

    public get worldScale() {
        return this._worldScale;
    }

    // ------------------------------------------------------------------ 狀態查詢

    /** 粒子系統是否已建立完成、可以接受操作。 */
    public abstract isReady(): boolean;

    // 粒子系統"本身" 是否暫停
    public isPaused() {
        if (this.isReady()) {
            return this.areAllEmittersPaused();
        }
        return this._pause;
    }

    // 粒子"發射器"是否暫停
    public isEmitterPaused() {
        if (this.isReady()) {
            return this.areGeneratorsInAllEmittersPaused();
        }
        return this._pauseEmitter;
    }

    // 取得當前粒子數量
    public abstract getNumParticles(): number;

    // ------------------------------------------------------------------ 播放控制

    /**
     * 重新啟動粒子系統
     * @param options 可選參數, 用於指定重啟後粒子系統及發射器的暫停狀態
     */
    public restart(options?: { generatorsPaused: boolean; paused: boolean }) {
        options = {
            ...{
                generatorsPaused: this.isEmitterPaused(),
                paused: this.isPaused(),
            },
            ...options,
        };

        if (this.isReady()) {
            this.getNeutrinoWorldPositionRotation(this.nwpos, this.nwrot);
            this.restartEffect(this.nwpos, this.nwrot, options);
        }
        this.emit(NeutrinoComponentBase.EVENT_TYPE.Restart);
    }

    // 重設座標, 座標變換後立即調用此方法可避免觸發拖尾效果, 可用在重設節點狀態時
    public resetPositionRotation() {
        if (!this.isReady()) {
            return;
        }

        this.getNeutrinoWorldPositionRotation(this.nwpos, this.nwrot);
        this.resetEffectPosition(this.nwpos, this.nwrot);
        this.emit(NeutrinoComponentBase.EVENT_TYPE.ResetPosition);
    }

    // 暫停粒子系統
    public pauseSystem() {
        if (this.isReady()) {
            this.pauseAllEmitters();
        }
        this.emit(NeutrinoComponentBase.EVENT_TYPE.Paused);
    }

    // 恢復粒子系統暫停狀態
    public resumeSystem() {
        if (this.isReady()) {
            this.unpauseAllEmitters();
        }
        this.emit(NeutrinoComponentBase.EVENT_TYPE.Resumed);
    }

    // 暫停粒子發射器
    public pauseGeneratorsSystem() {
        if (this.isReady()) {
            this.pauseGeneratorsInAllEmitters();
        }
        this.emit(NeutrinoComponentBase.EVENT_TYPE.EmitterPaused);
    }

    // 恢復粒子發射器暫停狀態
    public resumeGeneratorsSystem() {
        if (this.isReady()) {
            this.unpauseGeneratorsInAllEmitters();
        }
        this.emit(NeutrinoComponentBase.EVENT_TYPE.EmitterResumed);
    }

    // ------------------------------------------------------------------ 發射器參數

    /** 設定自定義屬性至所有發射器 */
    public abstract setPropertyInAllEmitters(name: string, value: number | Vec2 | Vec3): void;

    /** 設定自定義屬性至指定發射器 */
    public abstract setEmitterProperty(emitterName: string, name: string, value: number | Vec2 | Vec3): void;

    /** 設定指定發射器的貼圖網格大小 */
    public abstract setEmitterSpriteGridSize(emitterName: string, rowCount: number, columnCount: number): void;

    /** 設定指定發射器的發射參數（generating_rate / burst_num / ...） */
    public abstract setEmitParameter(emitterName: string, propertyName: string, value: number): void;

    /** 依名稱取得發射器物件（回傳的是 runtime 內部物件，僅供進階用途） */
    public abstract getEmitter(emitterName: string): any;

    // ------------------------------------------------------------------ 材質

    public abstract getMaterialIndexByRenderStyleIndex(renderStyleIndex: number): number;

    public getMaterialsEnum() {
        if (this._materialsEnum) {
            return this._materialsEnum;
        }
        if (this.textureList?.length > 0) {
            const enumDef: { [key: string]: number } = {};
            for (let i = 0; i < this.textureList.length; i++) {
                const name = this.textureList[i];
                enumDef[name] = i;
            }
            this._materialsEnum = Enum(enumDef);
            return this._materialsEnum;
        }
        return null;
    }

    protected clearMaterialsEnum() {
        this._materialsEnum = null;
    }

    // ------------------------------------------------------------------ 世界座標

    public hasZeroWorldScale(): boolean {
        const worldScale = this._worldScale;

        // 引擎 Mat4.toRTS 會把 0 縮放墊成 1e-6 防除零，過小的門檻會漏判
        return Math.abs(worldScale.x) <= EPSILON || Math.abs(worldScale.y) <= EPSILON || Math.abs(worldScale.z) <= EPSILON;
    }

    protected getNeutrinoWorldPositionRotation(position: number[], rotation: number[]) {
        const worldMatrix = gnwprMat0;
        const worldRotation = gnwprQuat0;
        const worldPosition = gnwprVec0;
        const worldScale = this._worldScale;

        this.node.getWorldRotation(worldRotation);
        this.node.getWorldMatrix(worldMatrix);

        if (this._worldParent) {
            const worldParentMatrix = gnwprMat1;
            const worldParentRotation = gnwprQuat1;
            this.worldParent!.getWorldMatrix(worldParentMatrix);
            this.worldParent!.getWorldRotation(worldParentRotation);
            Quat.invert(worldParentRotation, worldParentRotation);
            Quat.multiply(worldRotation, worldRotation, worldParentRotation);
            Mat4.invert(worldParentMatrix, worldParentMatrix);
            Mat4.multiply(worldMatrix, worldParentMatrix, worldMatrix);
        }

        worldMatrix.getTranslation(worldPosition);
        worldMatrix.getScale(worldScale);

        if (this.hasZeroWorldScale()) {
            position[0] = worldPosition.x;
            position[1] = worldPosition.y;
            position[2] = worldPosition.z;
        } else {
            position[0] = worldPosition.x / worldScale.x;
            position[1] = worldPosition.y / worldScale.y;
            position[2] = worldPosition.z / worldScale.z;
        }

        rotation[0] = worldRotation.x;
        rotation[1] = worldRotation.y;
        rotation[2] = worldRotation.z;
        rotation[3] = worldRotation.w;
    }

    // ------------------------------------------------------------------ 生命週期

    public abstract resetInEditor(): void;

    // For UIRenderer only. Creates empty assembler.
    // eslint-disable-next-line @typescript-eslint/naming-convention
    protected _flushAssembler() {
        const assembler = NeutrinoComponentBase.Assembler.getAssembler(this);

        if (this._assembler !== assembler) {
            this._assembler = assembler;
        }
    }

    /**
     * 每幀在模擬更新後呼叫，負責在粒子全部消失時送出事件。
     * 子類別的 update() 完成模擬後呼叫它。
     */
    protected notifyParticleCount() {
        const totalParticles = this.getNumParticles();
        if (this.lastParticleCount > 0 && totalParticles === 0) {
            this.emit(NeutrinoComponentBase.EVENT_TYPE.ParticlesAllDisappear);
        }
        this.lastParticleCount = totalParticles;
    }

    // ------------------------------------------------------------------ 子類別實作

    protected abstract waitForContextAndReset(): void;
    protected abstract applyFps(value: number): void;
    protected abstract areAllEmittersPaused(): boolean;
    protected abstract areGeneratorsInAllEmittersPaused(): boolean;
    protected abstract pauseAllEmitters(): void;
    protected abstract unpauseAllEmitters(): void;
    protected abstract pauseGeneratorsInAllEmitters(): void;
    protected abstract unpauseGeneratorsInAllEmitters(): void;
    protected abstract restartEffect(position: number[], rotation: number[], options: { generatorsPaused: boolean; paused: boolean }): void;
    protected abstract resetEffectPosition(position: number[], rotation: number[]): void;

    protected abstract getEffectClass();
}
